/**
 * Calendar Store - UI state only.
 *
 * All calendar data (events, reminders) lives in the GraphQL backend and
 * is read via the react-query hooks in `hooks/useCalendar.ts`. This store
 * keeps only the ephemeral UI preferences that the user expects to
 * survive a page reload: which date is selected and which view mode
 * (month/week/day) is active. No business data, no localStorage mock.
 *
 * The `LocalCalendarEvent` / `LocalEventRecurrence` types that used to
 * live here have moved to `services/localCalendarEvent.ts` next to
 * `googleCalendarService.ts` (their primary consumer). The web row
 * components (`EventRow`, `ProjectsView`) import from the services
 * barrel.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createZustandStorage } from '../storage/adapter';
import type { ISODate } from '../domain/models';

interface CalendarState {
  selectedDate: ISODate | null;
  viewMode: 'month' | 'week' | 'day';
  setSelectedDate: (date: ISODate | null) => void;
  setViewMode: (mode: 'month' | 'week' | 'day') => void;
}

export const useCalendarStore = create<CalendarState>()(
  persist(
    (set) => ({
      selectedDate: null,
      viewMode: 'month',
      setSelectedDate: (date) => set({ selectedDate: date }),
      setViewMode: (mode) => set({ viewMode: mode }),
    }),
    {
      name: 'calendar-storage',
      storage: createZustandStorage(),
      partialize: (state) => ({
        selectedDate: state.selectedDate,
        viewMode: state.viewMode,
      }),
    }
  )
);
